import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CollectionLifecycle } from '@qkindex/contracts';
import { DialogActions } from '../../../components/ui/DialogActions';
import { DialogContent } from '../../../components/ui/DialogContent';
import {
  collectionDeletionErrorMessage,
  createCollectionDeletionController,
} from '../collectionDeletionService';

interface CollectionDeletionSectionProps {
  collectionId: string;
  userId: string;
  collectionName: string;
  lifecycle: CollectionLifecycle;
  onChanged(): Promise<void> | void;
  onCancel?: () => void;
}
export function CollectionDeletionSection({
  collectionId,
  userId,
  collectionName,
  lifecycle,
  onChanged,
  onCancel,
}: CollectionDeletionSectionProps) {
  const controller = useMemo(() => createCollectionDeletionController(), []);
  const scopeKey = `collection.delete.${collectionId}.${userId}`;
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const { requestClose, runAfterSave } = useUnsavedChangesGuard({
    scopeKey,
    open: lifecycle === 'active',
    dirty: false,
    pending: busy,
    onDiscard: () => {},
    onClose: () => onCancel?.(),
  });
  const dismiss = () => {
    void requestClose();
  };
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    controller.reset();
    setConfirmation('');
    setError(undefined);
    setBusy(false);
    return () => {
      generation.current += 1;
      controller.reset();
    };
  }, [collectionId, userId, collectionName, controller]);
  const run = async () => {
    if (busy || confirmation !== collectionName || lifecycle !== 'active')
      return;
    const requested = generation.current;
    setBusy(true);
    setError(undefined);
    try {
      await controller.delete(userId, collectionId, confirmation);
      if (generation.current !== requested) return;
      setConfirmation('');
      let completion = Promise.resolve();
      runAfterSave(() => {
        completion = Promise.resolve(onChanged());
      });
      await completion;
    } catch (failure) {
      if (generation.current === requested)
        setError(collectionDeletionErrorMessage(failure));
    } finally {
      if (generation.current === requested) setBusy(false);
    }
  };
  if (lifecycle !== 'active') return null;
  return (
    <>
      <DialogContent>
        <Stack spacing={3}>
          <Typography>
            Are you sure you want to delete the{' '}
            <Box component="strong">
              <bdi dir="auto">{collectionName}</bdi>
            </Box>{' '}
            collection? This action is irreversible.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={`Enter ${collectionName} to delete`}
            value={confirmation}
            autoComplete="off"
            inputProps={{ spellCheck: false }}
            disabled={busy}
            onChange={(event) => setConfirmation(event.target.value)}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={dismiss}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy || confirmation !== collectionName}
          onClick={() => {
            void run();
          }}
        >
          {busy ? 'Deleting…' : 'Delete collection'}
        </Button>
      </DialogActions>
    </>
  );
}
